/**
 * Fund Release Letter — the bilingual (Hindi + English) sanction letter ICFRE
 * issues to a State Nodal Officer when an installment is released.
 *
 * The wording is a transcription of the approved Word template
 * ("Fund release letter for portal.docx"); only the figures, dates and parcel
 * identifiers are substituted from the fund application being released, so the
 * admin never retypes them. The same source is exported as either a true DOCX
 * document (for numbering and e-signing) or a PDF (for review and sharing).
 *
 * Anything the portal does not hold (letter number, e-sign date, and the SNO's
 * bank account, which is not part of the fund application payload) is left as
 * the same dotted fill-in the source template uses.
 */

// Bundled rather than read out of `public/`, so the masthead is emitted into
// the build's `static/media/` alongside the code that needs it and cannot go
// missing from a deploy that carried the JavaScript across.
import letterheadAsset from "./icfre-letterhead.png";
import { saveAs } from "file-saver";

export type FundReleaseLetterFormat = "pdf" | "docx";

/** Dotted fill-in for a value the portal cannot supply, as in the template. */
const BLANK = "…………………………";

/** ICFRE's own account the payment is drawn from — fixed on the template. */
const ADMIN_ACCOUNT_NO = "496902010091029";

/**
 * Where the ICFRE masthead is read from, in order of preference.
 *
 * The bundled copy comes first: importing it puts the file under the build's
 * own `static/media/` with a content hash, so it ships and is cache-busted with
 * the JavaScript that needs it. The `public/` copy is kept as a fallback for
 * builds that still carry it there.
 */
const LETTERHEAD_URLS = [
  letterheadAsset,
  `${process.env.PUBLIC_URL}/assets/images/icfre-letterhead.png`,
];

/** First eight bytes of any PNG. */
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/* -------------------------------------------------------------------------- */
/* Number and date formatting                                                  */
/* -------------------------------------------------------------------------- */

/** 1510900 -> "15,10,900" (Indian grouping, whole rupees). */
const indianNumber = (value: number) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Math.round(value)
  );

const EN_ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const EN_TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

/** 0-99 in English. */
const enTwoDigits = (value: number): string => {
  if (value < 20) return EN_ONES[value];
  const tens = EN_TENS[Math.floor(value / 10)];
  const ones = EN_ONES[value % 10];
  return ones ? `${tens} ${ones}` : tens;
};

/** 1510900 -> "fifteen lakh ten thousand nine hundred", in the Indian scale. */
const enWordsRaw = (value: number): string => {
  const amount = Math.round(value);
  if (amount <= 0) return "zero";

  const parts: string[] = [];
  const crore = Math.floor(amount / 10000000);
  const lakh = Math.floor((amount % 10000000) / 100000);
  const thousand = Math.floor((amount % 100000) / 1000);
  const hundred = Math.floor((amount % 1000) / 100);
  const rest = amount % 100;

  if (crore) parts.push(`${enWordsRaw(crore)} crore`);
  if (lakh) parts.push(`${enTwoDigits(lakh)} lakh`);
  if (thousand) parts.push(`${enTwoDigits(thousand)} thousand`);
  if (hundred) parts.push(`${EN_ONES[hundred]} hundred`);
  if (rest) parts.push(enTwoDigits(rest));

  return parts.join(" ").toLowerCase();
};

/**
 * 1510900 -> "Fifteen lakh ten thousand nine hundred" — only the opening word
 * is capitalised, the casing the approved template is written in
 * ("Rupees Fifteen lakh ten thousand nine hundred Only").
 */
const enWords = (value: number): string => {
  const words = enWordsRaw(value);
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** 0-99 in Hindi — irregular throughout, so spelled out in full. */
const HI_0_99 = [
  "शून्य",
  "एक",
  "दो",
  "तीन",
  "चार",
  "पाँच",
  "छह",
  "सात",
  "आठ",
  "नौ",
  "दस",
  "ग्यारह",
  "बारह",
  "तेरह",
  "चौदह",
  "पंद्रह",
  "सोलह",
  "सत्रह",
  "अठारह",
  "उन्नीस",
  "बीस",
  "इक्कीस",
  "बाईस",
  "तेईस",
  "चौबीस",
  "पच्चीस",
  "छब्बीस",
  "सत्ताईस",
  "अट्ठाईस",
  "उनतीस",
  "तीस",
  "इकतीस",
  "बत्तीस",
  "तैंतीस",
  "चौंतीस",
  "पैंतीस",
  "छत्तीस",
  "सैंतीस",
  "अड़तीस",
  "उनतालीस",
  "चालीस",
  "इकतालीस",
  "बयालीस",
  "तैंतालीस",
  "चवालीस",
  "पैंतालीस",
  "छियालीस",
  "सैंतालीस",
  "अड़तालीस",
  "उनचास",
  "पचास",
  "इक्यावन",
  "बावन",
  "तिरेपन",
  "चौवन",
  "पचपन",
  "छप्पन",
  "सत्तावन",
  "अट्ठावन",
  "उनसठ",
  "साठ",
  "इकसठ",
  "बासठ",
  "तिरेसठ",
  "चौंसठ",
  "पैंसठ",
  "छियासठ",
  "सड़सठ",
  "अड़सठ",
  "उनहत्तर",
  "सत्तर",
  "इकहत्तर",
  "बहत्तर",
  "तिहत्तर",
  "चौहत्तर",
  "पचहत्तर",
  "छिहत्तर",
  "सतहत्तर",
  "अठहत्तर",
  "उनासी",
  "अस्सी",
  "इक्यासी",
  "बयासी",
  "तिरासी",
  "चौरासी",
  "पचासी",
  "छियासी",
  "सत्तासी",
  "अट्ठासी",
  "नवासी",
  "नब्बे",
  "इक्यानवे",
  "बानवे",
  "तिरानवे",
  "चौरानवे",
  "पचानवे",
  "छियानवे",
  "सत्तानवे",
  "अट्ठानवे",
  "निन्यानवे",
];

/** 1510900 -> "पंद्रह लाख दस हजार नौ सौ". */
const hiWords = (value: number): string => {
  const amount = Math.round(value);
  if (amount <= 0) return HI_0_99[0];

  const parts: string[] = [];
  const crore = Math.floor(amount / 10000000);
  const lakh = Math.floor((amount % 10000000) / 100000);
  const thousand = Math.floor((amount % 100000) / 1000);
  const hundred = Math.floor((amount % 1000) / 100);
  const rest = amount % 100;

  if (crore) parts.push(`${hiWords(crore)} करोड़`);
  if (lakh) parts.push(`${HI_0_99[lakh]} लाख`);
  if (thousand) parts.push(`${HI_0_99[thousand]} हजार`);
  if (hundred) parts.push(`${HI_0_99[hundred]} सौ`);
  if (rest) parts.push(HI_0_99[rest]);

  return parts.join(" ");
};

/** "1st", "2nd", "3rd", ... — matches the ordinal used elsewhere on the page. */
const enOrdinal = (value: number) => {
  const lastDigit = value % 10;
  const lastTwo = value % 100;
  if (lastDigit === 1 && lastTwo !== 11) return `${value}st`;
  if (lastDigit === 2 && lastTwo !== 12) return `${value}nd`;
  if (lastDigit === 3 && lastTwo !== 13) return `${value}rd`;
  return `${value}th`;
};

/** Feminine ordinal, for "किस्त" — "तीसरी किस्त". */
const HI_ORDINAL_F = [
  "",
  "पहली",
  "दूसरी",
  "तीसरी",
  "चौथी",
  "पाँचवीं",
  "छठी",
  "सातवीं",
  "आठवीं",
  "नौवीं",
  "दसवीं",
];

/** Oblique masculine ordinal, for "वर्ष" — "तीसरे वर्ष". */
const HI_ORDINAL_M = [
  "",
  "पहले",
  "दूसरे",
  "तीसरे",
  "चौथे",
  "पाँचवें",
  "छठे",
  "सातवें",
  "आठवें",
  "नौवें",
  "दसवें",
];

const hiOrdinal = (table: string[], value: number) =>
  table[value] ?? `${value}वीं`;

/** "2026-07-08T..." -> "08.07.2026", the date form the template is written in. */
const dottedDate = (value?: string | Date | null) => {
  if (!value) return BLANK;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return BLANK;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
  return parts.replace(/\//g, ".");
};

/** "2026-2027" / "2026-27" -> "2026-27"; anything else passes through. */
const shortFy = (value: string) =>
  value.trim().replace(/-(\d{2})(\d{2})$/, "-$2");

/** "2026-27" -> "2025-26", the year an unspent balance is carried in from. */
const previousFy = (value: string) => {
  const match = shortFy(value).match(/^(\d{4})-(\d{2})$/);
  if (!match) return BLANK;
  const start = Number(match[1]) - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
};

/** April-March financial year of a date, as "2026-27". */
const financialYearOf = (value?: string | Date | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const [month, , year] = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
    .format(date)
    .split("/")
    .map(Number);
  const start = month >= 4 ? year : year - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** A value the portal holds, or the template's dotted fill-in. */
const orBlank = (value?: string | number | null) => {
  if (value === null || value === undefined) return BLANK;
  const text = String(value).trim();
  return text ? escapeHtml(text) : BLANK;
};

/* -------------------------------------------------------------------------- */
/* Deriving the letter's values from a fund application                        */
/* -------------------------------------------------------------------------- */

/** Every value the letter interpolates, already formatted for printing. */
export type FundReleaseLetterValues = {
  /** Amount being released, in figures — "15,10,900". */
  amount: string;
  amountWordsEn: string;
  amountWordsHi: string;
  /** "3rd" / "तीसरी" / "तीसरे". */
  installmentEn: string;
  installmentHiF: string;
  installmentHiM: string;
  /** "2026-27". */
  financialYear: string;
  /** Financial year an unspent balance is carried in from — "2025-26". */
  carryForwardFromFy: string;
  /** Unspent balance carried forward, in figures. 0 when there is none. */
  carryForward: string;
  hasCarryForward: boolean;
  /** Date the SNO forwarded the request on the portal — "08.07.2026". */
  requestDate: string;
  state: string;
  epid: string;
  registrationNo: string;
  area: string;
  entity: string;
  /** Eco-Restoration Amount the release is debited against — "1,36,46,000". */
  totalEstimatedCost: string;
  division: string;
  /**
   * The State Nodal Officer's account, as maintained on their profile in the
   * Admin Portal. Each field falls back to the template's dotted fill-in when
   * the SNO has no bank record, or that field of it is blank.
   */
  accountName: string;
  bankName: string;
  accountNo: string;
  ifscCode: string;
  /** Used to name the downloaded file. */
  fileNameStem: string;
};

/**
 * Read the letter's values off a fund application detail response.
 *
 * `releasedAmount` is passed separately because the letter is generated from
 * the Release Fund panel *before* the release is recorded — the amount the
 * admin has typed into the form is the one that belongs on the letter. It falls
 * back to an already-recorded release, then to the amount requested.
 */
export const fundReleaseLetterValues = (
  projectData: any,
  releasedAmount?: number | string | null
): FundReleaseLetterValues => {
  const fund = projectData?.fundDetails;
  const land = fund?.landOwnership;
  const project = land?.projectDetail;
  // The SNO's bank account, resolved server-side from the nodal officer behind
  // this application. Null when they have no bank record on file yet — every
  // field then prints as the template's dotted fill-in for manual completion.
  const sno = projectData?.snoBankDetail;

  const typed = Number(releasedAmount);
  const amount =
    Number.isFinite(typed) && typed > 0
      ? typed
      : Number(fund?.totalAmountReleased ?? 0) || Number(fund?.totalCost ?? 0);

  const year = Number(fund?.yearOfFundApplication ?? 0);

  const financialYear =
    shortFy(fund?.financialYear ?? "") ||
    financialYearOf(fund?.applicationDate ?? fund?.created_at) ||
    BLANK;

  // What the previous FA's utilization certificate left unspent — the same
  // figure the UC panel carries into this application's balance.
  const carryForward = Number(projectData?.previousFaUnutilizedAmount ?? 0);

  const area = land?.netPlantationArea ?? land?.totalArea;

  return {
    amount: indianNumber(amount),
    amountWordsEn: enWords(amount),
    amountWordsHi: hiWords(amount),
    installmentEn: year ? enOrdinal(year) : BLANK,
    installmentHiF: year ? hiOrdinal(HI_ORDINAL_F, year) : BLANK,
    installmentHiM: year ? hiOrdinal(HI_ORDINAL_M, year) : BLANK,
    financialYear,
    carryForwardFromFy: previousFy(financialYear),
    carryForward: indianNumber(carryForward),
    hasCarryForward: carryForward > 0,
    requestDate: dottedDate(fund?.raisedDate),
    state: orBlank(project?.address?.stateDetail?.name),
    epid: orBlank(fund?.landOwnershipEpid),
    registrationNo: orBlank(project?.registration_no),
    area: area == null || area === "" ? BLANK : escapeHtml(String(area)),
    entity: orBlank(land?.user?.firstName),
    totalEstimatedCost: indianNumber(Number(land?.demandNote ?? 0)),
    division: orBlank(project?.landSpecification?.division),
    accountName: orBlank(sno?.accountName),
    bankName: orBlank(sno?.bankName),
    accountNo: orBlank(sno?.accountNumber),
    ifscCode: orBlank(sno?.ifscCode),
    fileNameStem: [
      "Fund-Release-Letter",
      fund?.landOwnershipEpid,
      year ? `FA${year}` : "",
    ]
      .filter(Boolean)
      .join("_"),
  };
};

/* -------------------------------------------------------------------------- */
/* The letter itself                                                           */
/* -------------------------------------------------------------------------- */

/** The 16 terms the release is subject to, numbered i. — xvi. in the template. */
const TERMS_HI = [
  "ग्रीन क्रेडिट कार्यक्रम के क्रियान्वयन हेतु समय-समय पर पर्यावरण, वन एवं जलवायु परिवर्तन मंत्रालय द्वारा जारी अधिसूचनाओं एवं दिशा-निर्देशों के अनुरूप तथा सरकारी लेखा नियम, 1990, सामान्य वित्तीय नियम, 2017 एवं संबंधित राज्य/संघ राज्य क्षेत्र सरकार के लागू वित्तीय नियमों का अनुपालन करते हुए पारिस्थितिक पुनर्स्थापन गतिविधियाँ संपादित की जाएँगी।",
  "धनराशि का उपयोग केवल अवनत वनों के पारिस्थितिक पुनर्स्थापन हेतु GCP पोर्टल पर अपलोड की गई स्थल-विशिष्ट स्वीकृत लागत प्राक्कलन के अनुसार तथा सक्षम प्राधिकारी द्वारा प्रत्यायोजित शक्तियों के अंतर्गत प्रदान की गई प्रशासनिक एवं वित्तीय स्वीकृतियों सहित निर्धारित वित्तीय प्रक्रियाओं का पालन करते हुए किया जाएगा।",
  "ग्रीन क्रेडिट कार्यक्रम के अंतर्गत राज्य द्वारा संचालित सभी खातों का अभिलेख स्वीकृति प्रदान करने वाले प्राधिकारी तथा भारत के नियंत्रक एवं महालेखा परीक्षक द्वारा निरीक्षण एवं लेखा-परीक्षण हेतु उपलब्ध रहेगा तथा धनराशि स्वीकृति से संबंधित प्रत्येक आदेश में इस आशय का प्रावधान अनिवार्य रूप से शामिल किया जाएगा।",
  "ग्रीन क्रेडिट कार्यक्रम के अंतर्गत विभिन्न कार्यों हेतु श्रमिकों की नियुक्ति सहित सभी भुगतान प्रत्यक्ष लाभ अंतरण (DBT) के माध्यम से किए जाएँगे तथा किसी भी स्थिति में नकद भुगतान नहीं किया जाएगा।",
  "आगामी किश्त जारी किए जाने से पूर्व राज्य नोडल अधिकारी द्वारा पूर्व में जारी धनराशि का उपयोगिता प्रमाण-पत्र, विधिवत लेखा-परीक्षित व्यय विवरण तथा भौतिक प्रगति प्रतिवेदन GCP पोर्टल के माध्यम से प्रशासक को प्रस्तुत किया जाएगा।",
  "ठेकेदारों को भुगतानों के मामलों में TDS/GST तथा करयोग्य वस्तुओं के मामलों में GST जमा किए जाने के साक्ष्य सुरक्षित रखे जाएँगे।",
  "पारिस्थितिक पुनर्स्थापन कार्य पूर्ण होने पर राज्य नोडल अधिकारी द्वारा इस संबंध में प्रशासक को प्रतिवेदन प्रस्तुत किया जाएगा तथा पारिस्थितिक पुनर्स्थापन पूर्णता प्रमाण-पत्र जारी किया जाएगा।",
  "पारिस्थितिक पुनर्स्थापन से संबंधित जर्नल/अभिलेख/विवरण प्रशासक द्वारा निर्धारित मानक प्रारूप में ऑफलाइन अथवा ऑनलाइन संधारित किए जाएँगे।",
  "पारिस्थितिक पुनर्स्थापन गतिविधियों के प्रत्येक चरण के भू-अंकित (Geo-tagged) फोटोग्राफ लिए जाएँगे तथा GCP पोर्टल पर अपलोड किए जाएँगे।",
  "क्रियान्वयन अभिकरण अर्थात् राज्य वन विभाग के क्षेत्रीय अधिकारियों द्वारा सघन अनुश्रवण किया जाएगा तथा यह सुनिश्चित करने के लिए कि GCP के अंतर्गत प्रचलित कार्यप्रणाली, दिशा-निर्देशों एवं नियमों का कोई उल्लंघन न हो, निर्धारित प्रारूप में GCP पोर्टल पर प्रतिवेदन प्रस्तुत किया जाएगा।",
  "कार्यालय ज्ञापन संख्या F.No. HSM 12/24/2023-HSM (Pt.2)-Part (1), E220847, दिनांक 22.02.2024 तथा MoEF&amp;CC द्वारा समय-समय पर जारी अन्य संबंधित निर्देशों के अनुसार एक अनुश्रवण समिति का गठन किया जाएगा, जो प्रशासक द्वारा निर्धारित प्रारूप में GCP पोर्टल पर प्रत्येक छह माह में अनुश्रवण प्रतिवेदन प्रस्तुत करेगी।",
  "यदि क्रियान्वयन अभिकरण द्वारा धनराशि का उपयोग स्वीकृत उद्देश्य के अतिरिक्त किसी अन्य प्रयोजन हेतु किया जाता है, तो उक्त धनराशि ब्याज सहित वापस की जाएगी।",
  "ग्रीन क्रेडिट कार्यक्रम के अंतर्गत संचालित गतिविधियों का केंद्र अथवा राज्य सरकार की अन्य योजनाओं/प्रचलित कार्यक्रमों की गतिविधियों के साथ किसी प्रकार का दोहराव (Overlapping) नहीं होना चाहिए।",
  "MoEF&amp;CC के D.O. NO.8/47/2024-FP, दिनांक 18 जून, 2024 एवं 09 अगस्त, 2024 के अनुसार ड्रोन, वाहन आदि जैसी किसी भी अवसंरचनात्मक सामग्री की खरीद नहीं की जाएगी।",
  "ब्याज की राशि को आगामी निर्देश जारी होने तक संबंधित बैंक खाते में सुरक्षित रखा जाएगा।",
  "चालू वित्तीय वर्ष की 31 मार्च तक SNO के पास उपलब्ध अव्ययित शेष राशि का उपयोग अगले वित्तीय वर्ष में पुनर्वैधीकरण प्राप्त किए बिना नहीं किया जाएगा। इस संबंध में पुनर्वैधीकरण का प्रस्ताव अगले वित्तीय वर्ष के मई माह तक अनिवार्य रूप से प्रस्तुत किया जाएगा।",
];

const TERMS_EN = [
  "The eco-restoration activities shall be carried out in accordance with the notifications and guidelines issued by the MoEF&amp;CC from time to time for implementation of the Green Credit Programme, in compliance with the Government Accounting Rules, 1990, the General Financial Rules, 2017, and the applicable financial rules of the respective State/UT Government.",
  "The funds shall be utilized strictly as per the site-specific approved cost estimate uploaded on the GCP Portal for eco-restoration of degraded forests and in accordance with the prescribed financial procedures, including administrative and financial sanctions under the delegated powers of the competent authority.",
  "The all accounts of the State created under Green Credit Programme shall be open to inspection by the sanctioning authority and audit, by the Comptroller and Auditor General of India and a provision to this effect should invariably be incorporated in all orders sanctioning the amount.",
  "All payments including engagement of labour for various works under Green Credit Programme are to be made by Direct Bank Transfer (DBT) and no cash payment is to be made.",
  "Utilization certificates, duly audited expenditure statement and physical progress reports of the last fund release shall be submitted by the SNO to the Administrator through GCP Portal prior to release of any subsequent installments.",
  "Evidence of deposit of TDS/GST in case of contract payments and GST in case of leviable goods should be maintained.",
  "On completion of eco-restoration work, the SNO shall submit a report in this regard to the Administrator and issue a certificate of completion of eco-restoration.",
  "Eco-restoration journals/records/details shall be maintained offline/online in a standard format as prescribed by the Administrator.",
  "Geo-tagged photographs of every stage of eco-restoration activities will be taken and uploaded on the GCP Portal.",
  "Intensive monitoring by the field officers of the Implementing Agency i.e. SFD shall be carried out and reporting shall be done in a prescribed format on the GCP portal to ensure that there is no violation of prevailing methodology, guidelines, rules, etc. under GCP.",
  "A monitoring committee shall be constituted as per O.M. F.No. HSM 12/24/2023-HSM (pt2)-Part (1), E220847, dated 22.02.2024 and other related directions issued by MoEFCC time to time, and monitoring committee shall submit half yearly monitoring reports on the GCP portal, as prescribed by the Administrator.",
  "If the implementing agency does not use the fund for the purpose it was given, it has to be refunded with interest.",
  "There should not be any overlapping of activates under the GCP with the activities/ongoing schemes of Central/State Government.",
  "No infrastructure components such as drones and vehicles should be purchased as per the MoEF&amp;CC D.O. No. 8/47/2024-FP, dated 18th June, 2024 and 9th August, 2024.",
  "The interest amount may be kept in the bank account until further directions are issued.",
  "The unspent balance available with the SNO as on 31st March of the current financial year should not be utilized in the next financial year without seeking the re-validation. The re-validation proposal in this regard shall be submitted latest by May of the next financial year.",
];

const ROMAN = [
  "i",
  "ii",
  "iii",
  "iv",
  "v",
  "vi",
  "vii",
  "viii",
  "ix",
  "x",
  "xi",
  "xii",
  "xiii",
  "xiv",
  "xv",
  "xvi",
];

/** Roman-numbered terms. A table keeps the hanging indent intact in Word. */
const termsList = (items: string[]) =>
  items
    .map(
      (item, index) => `
      <tr style="page-break-inside:avoid">
        <td class="rn">${ROMAN[index] ?? index + 1}.</td>
        <td class="jt">${item}</td>
      </tr>`
    )
    .join("");

/**
 * A bulleted label/value block (parcel identifiers, account details). Rows are
 * held together so a long entity name never lands alone on the next page.
 */
const labelledRows = (rows: [string, string][]) =>
  rows
    .map(
      ([label, value]) => `
      <tr style="page-break-inside:avoid">
        <td class="bul">&bull;</td>
        <td class="lbl">${label}</td>
        <td class="val">${value}</td>
      </tr>`
    )
    .join("");

/** Blank lines left for the signature, as the template does. */
const signatureGap = (count: number) =>
  Array.from({ length: count }, () => "<p>&nbsp;</p>").join("");

/**
 * The complete letter as a Word-openable HTML document. `letterhead` is a data
 * URI so the saved file carries the ICFRE masthead with it rather than pointing
 * back at the portal.
 *
 * PDF retains the approved template dimensions. DOCX uses a wider text column
 * and compact, exact line spacing because Word otherwise expands the 16 terms
 * tables and pushes each language over multiple pages.
 */
export const buildFundReleaseLetterHtml = (
  values: FundReleaseLetterValues,
  letterhead: string,
  format: FundReleaseLetterFormat = "pdf"
) => {
  const pageMargins =
    format === "docx"
      ? "1.27cm 1.27cm 0.32cm 1.27cm"
      : "0.16cm 2.25cm 0.32cm 2.54cm";
  const contentWidth = format === "docx" ? "18.46cm" : "16.21cm";
  const hindiFontSize = format === "docx" ? "13pt" : "14.5pt";
  const hindiLineHeight = format === "docx" ? "13pt" : "1.16";
  const englishFontSize = format === "docx" ? "11pt" : "11.5pt";
  const englishLineHeight = format === "docx" ? "11pt" : "1.14";
  const docxTableTypography =
    format === "docx"
      ? `
  /* Word does not inherit a surrounding div's type settings into tables. */
  .hi table, .hi td {
    font-family: Kokila, "Nirmala UI", Mangal, Aparajita, "Arial Unicode MS", serif;
    font-size: ${hindiFontSize};
    line-height: ${hindiLineHeight};
  }
  .en table, .en td {
    font-family: "Times New Roman", Times, serif;
    font-size: ${englishFontSize};
    line-height: ${englishLineHeight};
  }`
      : "";

  // The masthead PNG is a fixed 1765x405px asset. Word's HTML renderer does
  // not reliably honour a percentage width on <img> — it falls back to the
  // native pixel size, which is nearly 47cm wide and blows straight through
  // the page. Pinning both the attributes and the inline style to the page's
  // actual usable width (21cm page - 2.54cm left margin - 2.25cm right
  // margin = 16.21cm) is what Word actually respects.
  const head = letterhead
    ? `<p class="hd"><img src="${letterhead}" alt="ICFRE" width="612" height="141" style="width:16.2cm;height:3.72cm" /></p>`
    : "";

  // The carry-forward sentence only appears when a previous year actually left
  // something unspent; on a first installment the template's sentence would be
  // untrue, so it is dropped.
  const carryForwardHi = values.hasCarryForward
    ? ` वित्तीय वर्ष ${values.carryForwardFromFy} से ₹${values.carryForward}/- की अव्ययित राशि को वित्तीय वर्ष ${values.financialYear} में आगे ले जाने की अनुमति भी दी जाती है।`
    : "";

  const carryForwardEn = values.hasCarryForward
    ? ` The permission is also granted to carry forward the unspent amount of ₹${values.carryForward}/- from the financial year ${values.carryForwardFromFy} to the financial year ${values.financialYear}.`
    : "";

  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<title>Fund Release Letter</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
<style>
  @page WordSection1 {
    size: 21cm 29.7cm;
    margin: ${pageMargins};
  }
  div.WordSection1 { page: WordSection1; width: ${contentWidth}; }

  body { margin: 0; color: #000; }
  p { margin: 0; }
  table { border-collapse: collapse; }

  /* Devanagari half — Kokila is the template's face; the fallbacks are the
     other Devanagari faces that ship with Windows. */
  .hi {
    font-family: Kokila, "Nirmala UI", Mangal, Aparajita, "Arial Unicode MS", serif;
    font-size: ${hindiFontSize};
    line-height: ${hindiLineHeight};
  }
  .en {
    font-family: "Times New Roman", Times, serif;
    font-size: ${englishFontSize};
    line-height: ${englishLineHeight};
  }
  ${docxTableTypography}

  .hd { text-align: center; margin-bottom: 4pt; }
  .hd img { width: 16.2cm; height: 3.72cm; display: block; margin: 0 auto; }

  .meta { width: 100%; margin-bottom: 6pt; page-break-inside: avoid; }
  .meta td { padding: 0; }
  .meta .r { text-align: right; }

  .addr { margin-left: 1.27cm; }

  /* "विषय:" / "Sub:" hangs to the left of the wrapped subject line. */
  .sub {
    font-weight: bold;
    text-align: justify;
    margin: 6pt 0 6pt 1.27cm;
    text-indent: -1.27cm;
  }

  .lead { text-align: justify; text-indent: 0.95cm; margin-bottom: 4pt; }

  /* Parcel identifiers and account details. */
  .kv { width: 100%; margin: 0 0 5pt 0; page-break-inside: avoid; }
  .kv td { padding: 0; vertical-align: top; }
  .kv .bul { width: 0.5cm; padding-left: 1.1cm; }
  .kv .lbl { padding-right: 12pt; white-space: nowrap; }
  .kv .val { width: 100%; }

  /* Numbered paragraphs 2-5. Hindi hangs the number; English indents it. */
  .np { text-align: justify; margin-bottom: 3pt; }
  .hi .np { margin-left: 0.5cm; text-indent: -0.5cm; }
  .en .np { text-indent: 0.95cm; }

  .terms { margin: 0 0 5pt 0.95cm; }
  .terms .rn { padding: 0 6pt 3pt 0; vertical-align: top; white-space: nowrap; }
  .terms .jt { padding: 0 0 3pt 0; text-align: justify; vertical-align: top; }

  .sign { text-align: right; margin-top: 2pt; page-break-inside: avoid; }
  .copy { margin-top: 6pt; page-break-inside: avoid; }
  .copy ol { margin: 0 0 0 16pt; padding: 0; }
  .copy li { text-align: justify; }
  .docx-language-page-break {
    page-break-before: always;
    break-before: page;
    height: 1pt;
    font-size: 1pt;
    line-height: 1pt;
  }
</style>
</head>
<body>
<div class="WordSection1">

<div class="hi">

${head}

<table class="meta"><tr>
  <td>सं. As per e-sign</td>
  <td class="r">दिनांक: ई-हस्ताक्षर के अनुसार</td>
</tr></table>

<p>सेवा में,</p>
<p class="addr">राज्य नोडल अधिकारी</p>
<p class="addr">ग्रीन क्रेडिट कार्यक्रम</p>
<p class="addr">पर्यावरण एवं वन विभाग, ${values.state}</p>

<p class="sub">विषय: ${
    values.state
  } में &ldquo;ग्रीन क्रेडिट कार्यक्रम के अंतर्गत पारिस्थितिक पुनर्स्थापन गतिविधियों&rdquo; के लिए ₹${
    values.amount
  } की निधि की ${values.installmentHiF} किस्त (वित्तीय वर्ष ${
    values.financialYear
  }) जारी करने के संदर्भ में।</p>

<p>महोदय,</p>

<p class="lead">यह सूचित किया जाता है कि महानिदेशक, भारतीय वानिकी अनुसंधान एवं शिक्षा परिषद, देहरादून (भा.वा.अ.शि.प.) ने निम्नलिखित भूखंड पर ${
    values.installmentHiM
  } वर्ष में प्रस्तावित पारिस्थितिक पुनर्स्थापन गतिविधियों को क्रियान्वित करने के लिए ₹${
    values.amount
  }/- (${values.amountWordsHi} रुपये मात्र) की निधि को ${
    values.installmentHiF
  } किस्त के रूप में जारी करने की स्वीकृति दी है। यह निधि आपके द्वारा जीसीपी पोर्टल पर प्रस्तुत लागत अनुमान और जीसीपी पोर्टल के माध्यम से ${
    values.requestDate
  } को प्रस्तुत निधि अनुरोध के अनुसार जारी की जा रही है।${carryForwardHi}</p>

<table class="kv">${labelledRows([
    ["EPID No.:", values.epid],
    ["भूखंड पंजीकरण संख्या:", values.registrationNo],
    ["भूखंड का क्षेत्रफल:", `${values.area} हेक्टेयर`],
    ["संस्था:", values.entity],
  ])}</table>

<p class="np">2.&nbsp;&nbsp;यह राशि चिन्हित क्षरित वन भूमि के पारिस्थितिक पुनर्स्थापन के लिए कुल अनुमानित लागत, अर्थात् ₹${
    values.totalEstimatedCost
  } में से डेबिट की जाएगी और उपर्युक्त भूखंड के इको-रेस्टोरेशन के लिए प्रस्तुत लागत अनुमान के अनुसार पारिस्थितिक पुनर्स्थापन कार्य करने के लिए उपयोग की जाएगी।</p>

<p class="np">3.&nbsp;&nbsp;महानिदेशक, भा.वा.अ.शि.प. एतद्द्वारा लेखा अधिकारी, भा.वा.अ.शि.प. को ₹${
    values.amount
  }/- के भुगतान हेतु बिल तैयार करने और प्रस्तुत करने के लिए अधिकृत करती हैं तथा यूनियन बैंक ऑफ इंडिया, वन अनुसंधान संस्थान, देहरादून को ग्रीन क्रेडिट कार्यक्रम प्रशासक खाता संख्या ${ADMIN_ACCOUNT_NO} से राज्य नोडल अधिकारी जीसीपी, ${
    values.state
  } के पक्ष में इलेक्ट्रॉनिक रूप से भुगतान करने का निर्देश देती हैं, जिनके खाते का विवरण नीचे दिया गया है:</p>

<table class="kv">${labelledRows([
    ["खाते का नाम:", values.accountName],
    ["बैंक का नाम:", values.bankName],
    ["खाता संख्या:", values.accountNo],
    ["IFSC कोड:", values.ifscCode],
    ["राशि:", `₹${values.amount}/-`],
  ])}</table>

<p class="np">4.&nbsp;&nbsp;उपर्युक्त राशि राज्य वन विभाग द्वारा जीसीपी पोर्टल पर अपलोड किए गए लागत अनुमान के अनुसार खर्च की जाएगी और जीसीपी प्रशासक की पूर्व सहमति के बिना स्वीकृत व्यय मदों से कोई विचलन नहीं किया जाएगा।</p>

<p class="np">5.&nbsp;&nbsp;धनराशि का निर्गमन निम्नलिखित नियमों एवं शर्तों के अधीन होगा:</p>

<table class="terms">${termsList(TERMS_HI)}</table>

<p class="np">यह सक्षम प्राधिकारी की स्वीकृति से जारी किया जाता है।</p>

<div class="sign">
  <p>भवदीय,</p>
  ${signatureGap(2)}
  <p>निदेशक, अंतर्राष्ट्रीय सहयोग</p>
  <p>भा.वा.अ.शि.प., देहरादून</p>
</div>

<div class="copy">
  <p>प्रतिलिपि:</p>
  <ol>
    <li>लेखा अधिकारी, भा.वा.अ.शि.प, देहरादून को सूचना और आवश्यक कार्रवाई के लिए।</li>
    <li>सूचना के लिए कार्यान्वयन अधिकारी, ${values.division} प्रभाग, ${
    values.state
  } को जी.सी.पी. पोर्टल के माध्यम से सूचित किया जाता है।</li>
  </ol>
</div>

</div>

<p class="docx-language-page-break" style="page-break-before:always;height:1pt;font-size:1pt;line-height:1pt">&nbsp;</p>

<div class="en">

${head}

<table class="meta"><tr>
  <td>Letter No. As per e-sign</td>
  <td class="r">Dated: As per E-sign</td>
</tr></table>

<p>To</p>
<p class="addr">The State Nodal Officer</p>
<p class="addr">Green Credit Programme</p>
<p class="addr">Environment and Forest Department, ${values.state}</p>

<p class="sub">Sub: Release of fund of ₹${values.amount} as ${
    values.installmentEn
  } installment (FY ${
    values.financialYear
  }) for the &ldquo;Eco-restoration activities under the Green Credit Programme&rdquo; in ${
    values.state
  } -reg.</p>

<p>Sir,</p>

<p class="lead">It is to convey the approval of the Director General, Indian Council of Forestry Research &amp; Education, Dehradun for the release of fund of ₹${
    values.amount
  }/- (Rupees ${values.amountWordsEn} Only) as ${
    values.installmentEn
  } installment to execute the eco-restoration activities proposed in the ${
    values.installmentEn
  } year on the following land parcel as per the cost estimate submitted by you on the GCP Portal and subsequent fund request submitted through GCP Portal ${
    values.requestDate
  }.${carryForwardEn}</p>

<table class="kv">${labelledRows([
    ["EPID No.:", values.epid],
    ["Land Parcel Reg. No.:", values.registrationNo],
    ["Area of Land Parcel:", `${values.area} ha`],
    ["Entity:", values.entity],
  ])}</table>

<p class="np">2.&nbsp;&nbsp;The amount will be debitable under total estimated cost i.e. ₹${
    values.totalEstimatedCost
  }/- for eco-restoration of identified degraded forest land and shall be used for carrying out the eco-restoration work as per the cost estimate submitted for the above-mentioned land parcel.</p>

<p class="np">3.&nbsp;&nbsp;The Director General, ICFRE hereby authorize the Account Officer, ICFRE to prepare and submit the bill for payment of ₹${
    values.amount
  }/- and to direct Union Bank of India, Forest Research Institute, Dehradun to make payment electronically from the Green Credit Programme Administrator A/c No. ${ADMIN_ACCOUNT_NO}, in favour of &ldquo;State Nodal Officer GCP, ${
    values.state
  }&rdquo; whose account details are given below:</p>

<table class="kv">${labelledRows([
    ["Name of Account:", values.accountName],
    ["Name of the Bank:", values.bankName],
    ["Account No.:", values.accountNo],
    ["IFSC Code:", values.ifscCode],
    ["Amount:", `₹${values.amount}/-`],
  ])}</table>

<p class="np">4.&nbsp;&nbsp;The aforesaid amount shall be spent in accordance with the cost estimates uploaded by SFD on GCP Portal and no deviation from approved items of expenditure shall be made without prior concurrence of the GCP Administrator.</p>

<p class="np">5.&nbsp;&nbsp;The release amount is subject to the following terms and conditions:</p>

<table class="terms">${termsList(TERMS_EN)}</table>

<p class="np">This is issued with the approval of the competent authority.</p>

<div class="sign">
  <p>Yours faithfully,</p>
  ${signatureGap(3)}
  <p>Director, International Cooperation</p>
  <p>ICFRE, Dehradun</p>
</div>

<div class="copy">
  <p>Copy to:</p>
  <ol>
    <li>The Account Officer, ICFRE, Dehradun for information and necessary action.</li>
    <li>The Implementing Officer, ${values.division} Division, ${
    values.state
  } through GCP Portal for information.</li>
  </ol>
</div>

</div>

</div>
</body>
</html>`;
};

/** Reads one candidate URL as a data URI, or "" if it is not a PNG. */
const readLetterheadFrom = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) return "";

  const blob = await response.blob();

  // A single-page-app host answers an unknown path with index.html and a 200,
  // so `response.ok` alone does not mean the masthead came back — without this
  // the letter would carry a data URI holding a page of HTML, which Word
  // renders as a broken image. Checked by signature rather than `blob.type`,
  // which a misconfigured server can report as octet-stream.
  const header = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  if (PNG_SIGNATURE.some((byte, index) => header[index] !== byte)) return "";

  return await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => resolve("");
    reader.readAsDataURL(blob);
  });
};

/**
 * Read the ICFRE masthead as a data URI, so the downloaded letter renders it
 * offline. Returns "" only when every candidate URL fails — the letter is still
 * worth issuing without the masthead, but the caller is told so it can say so
 * rather than handing over an unheaded sanction letter in silence.
 */
const loadLetterhead = async (): Promise<string> => {
  for (const url of LETTERHEAD_URLS) {
    try {
      const dataUri = await readLetterheadFrom(url);
      if (dataUri) return dataUri;
    } catch {
      // Try the next candidate.
    }
  }
  return "";
};

/**
 * Mount a measured copy of the letter for html2canvas/html2pdf.
 *
 * Passing a complete HTML document to html2pdf as a string causes it to place
 * `<html>`, `<head>` and `<body>` inside a temporary `<div>`. Browsers can then
 * drop the head styles, leaving the PDF as an unformatted stream of text. This
 * source keeps the style elements and body content in a valid, rendered DOM
 * tree while remaining far outside the visible viewport.
 */
const mountPdfSource = (html: string) => {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const host = document.createElement("div");
  const source = document.createElement("div");

  host.setAttribute("aria-hidden", "true");
  Object.assign(host.style, {
    position: "fixed",
    left: "-100000px",
    top: "0",
    width: "16.21cm",
    pointerEvents: "none",
    backgroundColor: "#ffffff",
  });

  Object.assign(source.style, {
    width: "16.21cm",
    margin: "0",
    color: "#000000",
    backgroundColor: "#ffffff",
  });

  parsed.head.querySelectorAll("style").forEach((style) => {
    source.appendChild(style.cloneNode(true));
  });
  parsed.body.childNodes.forEach((node) => {
    source.appendChild(node.cloneNode(true));
  });

  // DOCX needs a content-bearing paragraph for Word to honour a single page
  // break. PDF has its own explicit break before `.en`; removing this marker
  // prevents the two mechanisms from creating an extra blank PDF page.
  source.querySelectorAll(".docx-language-page-break").forEach((marker) => {
    marker.remove();
  });

  host.appendChild(source);
  document.body.appendChild(host);

  return { host, source };
};

/** Wait until fonts, the embedded letterhead and layout are ready to capture. */
const waitForPdfSource = async (source: HTMLElement) => {
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  await Promise.all(
    Array.from(source.querySelectorAll("img")).map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }
          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        })
    )
  );

  // Two frames allow style calculation and image sizing to settle before the
  // converter clones the source into its own capture container.
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
};

/**
 * Build the letter for this fund application and hand it to the browser in the
 * selected format. `releasedAmount` is the figure typed into the Release Fund
 * form, which is what belongs on the letter before the release is recorded.
 */
export const downloadFundReleaseLetter = async (
  projectData: any,
  releasedAmount: number | string | null | undefined,
  format: FundReleaseLetterFormat
): Promise<{ letterheadEmbedded: boolean }> => {
  const values = fundReleaseLetterValues(projectData, releasedAmount);
  const letterhead = await loadLetterhead();
  const html = buildFundReleaseLetterHtml(values, letterhead, format);

  if (format === "docx") {
    // Kept out of the initial admin bundle: document conversion is only
    // needed after the admin explicitly chooses this download.
    const { asBlob } = await import("html-docx-js-typescript");

    // Half-inch horizontal margins, expressed in twentieths of a point
    // (TWIPs), keep each language within its own two-page A4 section.
    const document = await asBlob(html, {
      orientation: "portrait",
      margins: { top: 720, right: 720, bottom: 181, left: 720 },
    });
    const blob =
      document instanceof Blob
        ? document
        : new Blob([document as ArrayBuffer], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });
    saveAs(blob, `${values.fileNameStem}.docx`);
  } else {
    // PDF rendering is similarly loaded on demand so this sizeable browser
    // utility does not slow every visit to Fund Application Details.
    const { default: html2pdf } = await import("html2pdf.js");

    // html2pdf renders the same styled HTML used by DOCX. The page break is on
    // the content-bearing English section so neither Word nor the PDF renderer
    // can discard it as an empty element.
    const { host, source } = mountPdfSource(html);
    try {
      await waitForPdfSource(source);
      await html2pdf()
        .set({
          // Keep a visible continuation-page gap while preserving enough
          // vertical room for each language to stay within two PDF pages.
          margin: [6.35, 22.5, 1.7, 25.4],
          filename: `${values.fileNameStem}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            scrollX: 0,
            scrollY: 0,
            windowWidth: source.scrollWidth,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: {
            mode: ["css", "legacy"],
            before: ".en",
            avoid: [".meta", ".kv", ".sign", ".copy", "tr"],
          },
        } as any)
        .from(source)
        .save();
    } finally {
      host.remove();
    }
  }

  return { letterheadEmbedded: !!letterhead };
};
